import { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { getRobertService, runRobertService } from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * Robert Johns — Fintrack's robot tester.
 *
 * Richard, 7 Oct 2026: "add a button to test now", "conducts the test there and
 * then", "one click and test commences. Then results emailed again."
 *
 * Robert opens Gameplan, the CRM and the Platform in a real browser and works down
 * a checklist. He does that by himself every morning at 8, and at no other time
 * unless he is asked (Richard, 7 Oct 2026: "only does test when 8am every day, and
 * upon me clicking Test Now"). This page is the asking: press Test now and the
 * checklist fills in below as he works, part by part, and is emailed when he has
 * finished.
 *
 * Nothing on this page can change anything in any system. Robert reads; he does not
 * write. The one button starts a test.
 */

const GREEN = "#1a7f37";
const RED = "#b0392e";
const AMBER = "#9a6700";

const panel = {
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
} as const;

const took = (seconds: number) => {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    if (s < 60) return `${s} seconds`;
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${m} min ${rest} s` : `${m} min`;
};

const verdict = (run: any) => {
    if (!run) return { text: "No test has been run yet.", colour: AMBER };
    if (run.working) return { text: `Robert is testing now — ${run.results?.length ?? 0} checks done so far.`, colour: "#1B4FE0" };
    if (run.stopped) return { text: "This run did not finish.", colour: RED };
    if (run.total === 0) return { text: "No checks ran.", colour: RED };
    if (run.failed > 0) return { text: `${run.failed} of ${run.total} checks FAILED`, colour: RED };
    return { text: `All ${run.total} checks passed`, colour: GREEN };
};

const Word = ({ line }: { line: any }) => {
    const word = line.skip ? "NOT RUN" : line.ok ? "PASS" : "FAIL";
    const colour = line.skip ? AMBER : line.ok ? GREEN : RED;
    return <span style={{ color: colour, fontWeight: 800, whiteSpace: "nowrap" }}>{word}</span>;
};

const CRMRobert = () => {
    const [data, setData] = useState<any>(null);
    const [problem, setProblem] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [picked, setPicked] = useState<number | null>(null);   // an older run, when one is clicked
    //  Pressed, and Robert has not shown up yet. This is state, not a note kept on the
    //  side: the first version kept the time of the press in a ref and worked out "just
    //  pressed" while drawing. React does not watch a ref, so the page never noticed it
    //  had become busy, never started looking for news, and sat on "Robert is starting…"
    //  for ever while he ran, finished and emailed. Found on the first live press, 7 Oct.
    const [waiting, setWaiting] = useState(false);

    const load = useCallback(async (quiet?: boolean) => {
        if (!quiet) setLoading(true);
        try {
            const r: any = await getRobertService();
            const d = r?.data ?? null;
            if (d && d.ok === false) {
                setProblem(d.message || "Robert could not be reached.");
            } else {
                setProblem("");
                setData(d);
            }
        } catch (e: any) {
            setProblem(e?.response?.data?.message || "Robert could not be reached.");
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runs: any[] = data?.runs ?? [];
    const latest = runs[0] ?? null;
    //  From the press until Robert shows up the page keeps looking, even if the engine
    //  has not said "starting" yet, so there is no gap where nothing moves.
    const active = !!(data?.starting || data?.working || waiting);

    //  He has shown up - or ninety seconds have gone and he has not, in which case the
    //  engine's own answer decides what the page says.
    useEffect(() => {
        if (waiting && data?.working) setWaiting(false);
    }, [waiting, data]);

    useEffect(() => {
        if (!waiting) return;
        const timer = setTimeout(() => setWaiting(false), 90000);
        return () => clearTimeout(timer);
    }, [waiting]);

    useEffect(() => {
        if (!active) return;
        const timer = setInterval(() => { load(true); }, 4000);
        return () => clearInterval(timer);
    }, [active, load]);

    const testNow = async () => {
        setBusy(true);
        try {
            const r: any = await runRobertService();
            const d = r?.data ?? {};
            if (d.ok) {
                setWaiting(true);
                setPicked(null);
                toast.success(d.message || "Robert has started.");
            } else {
                toast.error(d.message || "Robert could not be started.");
            }
            await load(true);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Robert could not be started.");
        } finally {
            setBusy(false);
        }
    };

    const shown = (picked !== null ? runs.find((x) => x.id === picked) : null) ?? latest;
    const starting = !!(data?.starting || (waiting && !data?.working)) && picked === null;
    const v = verdict(shown);
    const lines: any[] = shown?.results ?? [];
    const failed = lines.filter((l) => !l.ok && !l.skip);
    const notRun = lines.filter((l) => l.skip);

    let group = "";

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>Robert Johns</h3>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Robot tester. He checks everything is up, then Gameplan, the CRM and the Platform —
                        every morning at 8, and whenever you press Test now.
                    </div>
                </div>
                <Button color="primary" disabled={busy || active || loading || data?.connected === false} onClick={testNow}>
                    {busy ? "Starting…" : active ? "Testing…" : "Test now"}
                </Button>
            </div>

            {problem && (
                <div className="p-3 mb-3" style={{ ...panel, borderLeft: `6px solid ${RED}` }}>
                    <div className="f-w-700">{problem}</div>
                </div>
            )}

            {data?.connected === false && (
                <div className="p-3 mb-3" style={{ ...panel, borderLeft: `6px solid ${AMBER}` }}>
                    <div className="f-w-700">Test now is not connected yet.</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Robert still runs by himself at 8 am. The button needs one
                        GitHub key added to the SMS engine in Azure (ROBOT_GITHUB_TOKEN).
                    </div>
                </div>
            )}

            {loading && !data ? (
                <div className="p-4 text-center"><Spinner size="sm" /> Loading…</div>
            ) : (
                <>
                    {/* What is happening, or what happened */}
                    <div className="p-3 mb-3" style={{ ...panel, borderLeft: `6px solid ${starting ? "#1B4FE0" : v.colour}` }}>
                        {starting ? (
                            <>
                                <div className="f-w-700" style={{ fontSize: 16 }}>
                                    <Spinner size="sm" /> Robert is starting…
                                </div>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    GitHub takes about half a minute to give him a machine and a browser. The checklist
                                    fills in below as he works. The whole test takes about three minutes.
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="f-w-700" style={{ fontSize: 16, color: v.colour }}>
                                    {shown?.working && <Spinner size="sm" />} {v.text}
                                    {shown && !shown.working && notRun.length > 0 && (
                                        <span style={{ color: AMBER }}> · {notRun.length} not run</span>
                                    )}
                                </div>
                                {shown && (
                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                        {shown.started}
                                        {shown.trigger && <> · {shown.trigger}</>}
                                        {shown.finished && <> · took {took(shown.seconds)}</>}
                                        {shown.finished && (shown.emailed
                                            ? <> · emailed to {data?.emailTo || "help@fintrack.com.au"}</>
                                            : <> · not emailed</>)}
                                        {picked !== null && <> · <a href="#latest" onClick={(e) => { e.preventDefault(); setPicked(null); }}>back to the latest</a></>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* What failed, first, so it is not hunted for */}
                    {!starting && failed.length > 0 && (
                        <div className="p-3 mb-3" style={{ ...panel, borderLeft: `6px solid ${RED}` }}>
                            <h6 className="f-w-700" style={{ color: RED }}>What failed</h6>
                            <ol className="mb-0" style={{ paddingLeft: 20 }}>
                                {failed.map((l, i) => (
                                    <li key={i} style={{ marginBottom: 6 }}>
                                        <span className="f-w-700">{l.group} — {l.name}</span>
                                        <div className="text-muted" style={{ fontSize: 13 }}>{l.detail || "No detail."}</div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* The checklist */}
                    {!starting && lines.length > 0 && (
                        <div className="p-3 mb-3" style={panel}>
                            <h6 className="f-w-700">The checklist</h6>
                            <div className="table-responsive">
                                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                    <tbody>
                                        {lines.map((l, i) => {
                                            const head = l.group !== group;
                                            group = l.group;
                                            return [
                                                head && (
                                                    <tr key={`g${i}`}>
                                                        <td colSpan={4} style={{ background: "#eef1f6", fontWeight: 800 }}>{l.group}</td>
                                                    </tr>
                                                ),
                                                <tr key={i}>
                                                    <td style={{ width: 40, color: "#767d95" }}>{i + 1}</td>
                                                    <td>{l.name}</td>
                                                    <td style={{ width: 90 }}><Word line={l} /></td>
                                                    <td className="text-muted">{l.detail}</td>
                                                </tr>,
                                            ];
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* His last runs */}
                    {runs.length > 0 && (
                        <div className="p-3" style={panel}>
                            <h6 className="f-w-700">His last {runs.length} {runs.length === 1 ? "run" : "runs"}</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                    <thead>
                                        <tr>
                                            <th>When</th><th>Run</th><th>Result</th><th>Passed</th><th>Failed</th>
                                            <th>Not run</th><th>Took</th><th>Emailed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {runs.map((r) => {
                                            const rv = verdict(r);
                                            return (
                                                <tr key={r.id} style={{ cursor: "pointer", fontWeight: shown?.id === r.id ? 700 : 400 }}
                                                    onClick={() => setPicked(r.id === latest?.id ? null : r.id)}>
                                                    <td>{r.started}</td>
                                                    <td>{r.trigger}</td>
                                                    <td style={{ color: rv.colour, fontWeight: 700 }}>
                                                        {r.working ? "Testing…" : r.stopped ? "Did not finish" : r.failed > 0 ? "FAILED" : "Passed"}
                                                    </td>
                                                    <td>{Math.max(0, (r.total || 0) - (r.failed || 0))}</td>
                                                    <td style={{ color: r.failed > 0 ? RED : undefined }}>{r.failed}</td>
                                                    <td>{r.skipped}</td>
                                                    <td>{r.finished ? took(r.seconds) : "—"}</td>
                                                    <td>{r.emailed ? "Yes" : "No"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CRMRobert;
